import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '@/lib/api'
import { Badge } from './Badge'

interface UserResult {
  user_id: string
  name: string | null
  email: string
  tier: string
}

interface UserSearchInputProps {
  label: string
  value: string
  onChange: (userId: string, user: UserResult | null) => void
  placeholder?: string
}

const tierBadgeVariant: Record<string, 'brand' | 'success' | 'warning' | 'default'> = {
  admin: 'brand',
  paid: 'success',
  trial: 'warning',
  free: 'default',
}

export function UserSearchInput({ label, value, onChange, placeholder = 'Search by name or email...' }: UserSearchInputProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserResult[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<UserResult | null>(null)
  const [focusIndex, setFocusIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([])
      setOpen(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get<{ results: UserResult[] }>('/api/admin/users/search', {
        params: { q, limit: 10 },
      })
      setResults(res.results)
      setOpen(res.results.length > 0)
      setFocusIndex(-1)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  function handleInputChange(val: string) {
    setQuery(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val), 300)
  }

  function handleSelect(user: UserResult) {
    setSelected(user)
    onChange(user.user_id, user)
    setOpen(false)
    setQuery('')
    setResults([])
  }

  function handleClear() {
    setSelected(null)
    onChange('', null)
    setQuery('')
    setResults([])
    inputRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setFocusIndex(i => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setFocusIndex(i => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && focusIndex >= 0 && results[focusIndex]) {
      e.preventDefault()
      handleSelect(results[focusIndex])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Cleanup debounce on unmount
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
  }, [])

  return (
    <div>
      <label className="block text-xs text-[var(--content-tertiary)] mb-1">{label}</label>

      {selected ? (
        <div className="flex items-center gap-2 px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg">
          <div className="flex-1 min-w-0">
            <span className="font-medium text-[var(--content)]">{selected.name || selected.email}</span>
            {selected.name && (
              <span className="ml-2 text-[var(--content-tertiary)]">{selected.email}</span>
            )}
          </div>
          <Badge variant={tierBadgeVariant[selected.tier] ?? 'default'} className="shrink-0">{selected.tier}</Badge>
          <button
            type="button"
            onClick={handleClear}
            className="ml-1 text-[var(--content-tertiary)] hover:text-[var(--content)] cursor-pointer"
            aria-label="Clear selection"
          >
            ×
          </button>
        </div>
      ) : (
        <div ref={wrapperRef} className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => { if (results.length > 0) setOpen(true) }}
            placeholder={placeholder}
            className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-[var(--content-tertiary)] border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {open && (
            <ul className="absolute z-50 w-full mt-1 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto">
              {results.map((user, i) => (
                <li
                  key={user.user_id}
                  className={`px-3 py-2 cursor-pointer flex items-center gap-2 text-sm ${
                    i === focusIndex
                      ? 'bg-[var(--brand)]/10'
                      : 'hover:bg-[var(--surface-nested)]'
                  }`}
                  onMouseDown={() => handleSelect(user)}
                  onMouseEnter={() => setFocusIndex(i)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[var(--content)] truncate">
                      {user.name || 'No name'}
                    </div>
                    <div className="text-xs text-[var(--content-tertiary)] truncate">
                      {user.email}
                    </div>
                  </div>
                  <Badge variant={tierBadgeVariant[user.tier] ?? 'default'} className="shrink-0">
                    {user.tier}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Hidden display of resolved UUID for transparency */}
      {value && (
        <p className="mt-1 text-[10px] text-[var(--content-tertiary)] font-mono truncate">{value}</p>
      )}
    </div>
  )
}
